import React, { useEffect, useState } from 'react';
import { CalendarDays, CreditCard, Edit, Mail, Phone, ArrowRight, Zap, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import { useSiteContext } from "@/components/ruby-ai-v2/contexts/SiteContext";
import {
  getSubscriptionStatus,
  getUsage,
  openBillingPortal,
  type SubscriptionStatus,
  type UsageStatus,
  formatBillingDate,
} from "@/components/subscriptions/subscriptionService";

const GRADIENT = "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)";
const ACCENT = "var(--accent, #8B5CF6)";

function planLabel(tier?: string | null): string {
  if (!tier) return "No plan";
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function statusLabel(status?: SubscriptionStatus["status"]): string {
  if (status === "active") return "Active";
  if (status === "past_due") return "Past due";
  if (status === "canceled") return "Canceled";
  if (status === "incomplete") return "Incomplete";
  return "Inactive";
}

export function AccountOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSite, sites } = useSiteContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [usage, setUsage] = useState<UsageStatus | null>(null);
  const [formData, setFormData] = useState({ name: "", email: "", company: "", phone: "" });

  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      const meta = user.user_metadata || {};
      const { data: profile } = await supabase
        .from("users")
        .select("full_name,email")
        .eq("id", user.id)
        .maybeSingle();
      const [sub, credits] = await Promise.all([getSubscriptionStatus(), getUsage()]);
      if (!active) return;
      setFormData({
        name: profile?.full_name || meta.full_name || "",
        email: user.email || profile?.email || "",
        company: meta.company || "",
        phone: meta.phone || "",
      });
      setSubscription(sub);
      setUsage(credits);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveChanges = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { error: metaError } = await supabase.auth.updateUser({
        data: {
          full_name: formData.name.trim(),
          phone: formData.phone.trim(),
          company: formData.company.trim(),
        },
      });
      if (metaError) throw metaError;

      const { error: profileError } = await supabase
        .from("users")
        .update({ full_name: formData.name.trim() })
        .eq("id", user.id);
      if (profileError) console.warn("[account] profile name:", profileError.message);

      const nextEmail = formData.email.trim().toLowerCase();
      if (nextEmail && nextEmail !== (user.email || "").toLowerCase()) {
        const { error: emailError } = await supabase.auth.updateUser({ email: nextEmail });
        if (emailError) throw emailError;
        setFormData((prev) => ({ ...prev, email: user.email || "" }));
        toast.success("Check your inbox to confirm the new email address");
      } else {
        toast.success("Profile updated");
      }
      setIsDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save your profile");
    } finally {
      setSaving(false);
    }
  };

  const handleManageSubscription = async () => {
    const domain = activeSite?.domain || sites[0]?.domain;
    setPortalBusy(true);
    try {
      const res = await openBillingPortal({
        domain,
        returnUrl: `${window.location.origin}/account`,
      });
      if (res.ok) {
        window.location.href = res.url;
        return;
      }
      if (res.reason === "domain_required") {
        toast.error("Add a site first", { description: "The billing portal is opened for a site on your account." });
      } else if (res.reason === "not_configured") {
        toast.info("Billing portal is not configured in this environment yet");
      } else if (res.reason === "domain_not_owned") {
        toast.error("That site isn't on your account");
      } else {
        toast.error("Couldn't open the billing portal");
      }
    } finally {
      setPortalBusy(false);
    }
  };

  const displayName = formData.name || user?.email || "Account";
  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const plan = planLabel(subscription?.tier);
  const monthlyRemaining = usage?.included ?? 0;
  const boosterRemaining = usage?.boosterRemaining ?? 0;
  const totalCredits = usage?.balance ?? 0;

  return (
    <div>
      <div className="mb-5 rounded-2xl border border-white/[0.07] bg-og01-card p-6">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <div className="font-heading text-[20px] text-og01-text">Profile Information</div>
            <div className="mt-1 text-[13px] text-og01-text-secondary">Manage your account details and personal information</div>
          </div>
          <button onClick={() => setIsDialogOpen(true)}
            className="inline-flex items-center gap-2 rounded-[10px] border-none px-4 py-2 text-[13.5px] font-semibold text-white transition-all hover:brightness-105"
            style={{ background: GRADIENT, boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
            <Edit className="h-[15px] w-[15px]" />Edit Profile
          </button>
        </div>
        <div className="flex items-center gap-[22px]">
          <div className="grid h-[84px] w-[84px] flex-none place-items-center rounded-full text-[28px] font-semibold text-white" style={{ background: GRADIENT }}>
            {initials || "?"}
          </div>
          <div>
            <div className="mb-2 font-heading text-[26px] leading-[1.1] text-og01-text">{displayName}</div>
            <div className="mb-[5px] flex items-center gap-2 text-[13.5px] text-og01-text-secondary">
              <Mail className="h-[15px] w-[15px] text-og01-text-muted" />{formData.email || "—"}
            </div>
            <div className="mb-[5px] flex items-center gap-2 text-[13.5px] text-og01-text-secondary">
              <Phone className="h-[15px] w-[15px] text-og01-text-muted" />{formData.phone || "—"}
            </div>
            <span className="mt-2.5 inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{ color: ACCENT, background: `color-mix(in srgb, ${ACCENT} 14%, transparent)` }}>
              {loading ? "…" : `${plan} Plan`}
            </span>
          </div>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="rounded-2xl border border-white/[0.07] bg-og01-card p-6">
          <div className="mb-5">
            <div className="font-heading text-[20px] text-og01-text">Subscription Details</div>
            <div className="mt-1 text-[13px] text-og01-text-secondary">Your current plan and billing information</div>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.07] py-3.5 first:border-t-0 first:pt-0">
            <div className="flex items-center gap-2.5 text-[13.5px] text-og01-text-secondary"><CreditCard className="h-[15px] w-[15px] text-og01-text-muted" />Current Plan</div>
            <span className="rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{ color: ACCENT, background: `color-mix(in srgb, ${ACCENT} 14%, transparent)` }}>{loading ? "…" : plan}</span>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.07] py-3.5">
            <div className="flex items-center gap-2.5 text-[13.5px] text-og01-text-secondary"><CalendarDays className="h-[15px] w-[15px] text-og01-text-muted" />Status</div>
            <span className="font-medium text-og01-text">{loading ? "…" : subscription?.cancellationDate ? `${statusLabel(subscription.status)} · Cancellation scheduled` : statusLabel(subscription?.status)}</span>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.07] py-3.5">
            <div className="flex items-center gap-2.5 text-[13.5px] text-og01-text-secondary"><CalendarDays className="h-[15px] w-[15px] text-og01-text-muted" />Active Since</div>
            <span className="font-medium text-og01-text">{loading ? "…" : formatBillingDate(subscription?.currentPeriodStart)}</span>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.07] py-3.5">
            <div className="flex items-center gap-2.5 text-[13.5px] text-og01-text-secondary"><CreditCard className="h-[15px] w-[15px] text-og01-text-muted" />{subscription?.cancellationDate ? "Access Until" : "Next Billing"}</div>
            <span className="font-medium text-og01-text">{loading ? "…" : formatBillingDate(subscription?.cancellationDate || subscription?.nextBillingDate)}</span>
          </div>
          <button onClick={handleManageSubscription} disabled={portalBusy}
            className="mt-[18px] inline-flex w-full items-center justify-center gap-2 rounded-[10px] border-none py-2.5 text-[13.5px] font-semibold text-white transition-all hover:brightness-105 disabled:opacity-60"
            style={{ background: GRADIENT, boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
            {portalBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Manage Subscription <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <div className="rounded-2xl border border-white/[0.07] bg-og01-card p-6">
          <div className="mb-5">
            <div className="font-heading text-[20px] text-og01-text">AI Credits</div>
            <div className="mt-1 text-[13px] text-og01-text-secondary">Monthly allowance plus booster credits still available</div>
          </div>
          <div className="mb-2 flex items-baseline gap-2">
            <span className="font-heading text-[40px] leading-none text-og01-text">{loading ? "…" : Math.max(0, totalCredits)}</span>
            <span className="text-[13px] text-og01-text-secondary">credits available</span>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.07] py-3.5">
            <div className="flex items-center gap-2.5 text-[13.5px] text-og01-text-secondary"><Zap className="h-[15px] w-[15px] text-og01-text-muted" />Monthly remaining</div>
            <span className="font-medium text-og01-text">{loading ? "…" : monthlyRemaining}</span>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.07] py-3.5">
            <div className="flex items-center gap-2.5 text-[13.5px] text-og01-text-secondary"><Zap className="h-[15px] w-[15px] text-og01-text-muted" />Booster remaining</div>
            <span className="font-medium text-og01-text">{loading ? "…" : boosterRemaining}</span>
          </div>
          <div className="flex items-center justify-between border-t border-white/[0.07] py-3.5">
            <div className="flex items-center gap-2.5 text-[13.5px] text-og01-text-secondary"><CalendarDays className="h-[15px] w-[15px] text-og01-text-muted" />Monthly resets</div>
            <span className="font-medium text-og01-text">{loading ? "…" : formatBillingDate(usage?.resetsOn)}</span>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-white/[0.07] bg-og01-card p-6">
        <div className="mb-5">
          <div className="font-heading text-[20px] text-og01-text">Quick Links</div>
          <div className="mt-1 text-[13px] text-og01-text-secondary">Shortcuts to frequently used pages</div>
        </div>
        <div className="grid gap-2.5 md:grid-cols-3">
          {[
            { label: "Settings", route: "/settings" },
            { label: "Subscriptions", route: "/subscriptions" },
            { label: "Resource Vault", route: "/resource-vault" },
          ].map((link) => (
            <button key={link.label} onClick={() => navigate(link.route)}
              className="flex w-full items-center justify-between rounded-[11px] border border-white/[0.07] bg-[#0F0F18] px-4 py-3.5 text-sm font-medium text-og01-text transition-colors hover:border-white/[0.12] hover:bg-og01-card-hover">
              {link.label}<ArrowRight className="h-4 w-4 text-og01-text-muted" />
            </button>
          ))}
        </div>
      </div>

      {isDialogOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-5" onClick={() => setIsDialogOpen(false)}>
          <div className="absolute inset-0" style={{ background: "rgba(4,4,10,0.72)", backdropFilter: "blur(5px)" }} />
          <div className="relative w-full max-w-[440px] rounded-[18px] border border-white/[0.07] bg-og01-card p-7" onClick={e => e.stopPropagation()} style={{ boxShadow: "0 30px 80px -30px rgba(0,0,0,.7)" }}>
            <h3 className="font-heading text-[22px] text-og01-text">Edit profile</h3>
            <p className="mb-5 mt-1 text-[13px] text-og01-text-secondary">Make changes to your profile information here. A new email address needs a confirmation link.</p>
            <div className="space-y-3.5">
              {([
                { id: "name", label: "Name", type: "text" },
                { id: "email", label: "Email", type: "email" },
                { id: "company", label: "Company", type: "text" },
                { id: "phone", label: "Phone", type: "text" },
              ] as const).map((f) => (
                <div key={f.id}>
                  <label htmlFor={f.id} className="mb-1.5 block text-[13px] text-og01-text-secondary">{f.label}</label>
                  <input id={f.id} name={f.id} type={f.type} value={formData[f.id]} onChange={handleInputChange}
                    className="w-full rounded-[10px] border border-white/[0.07] bg-[#0F0F18] px-3.5 py-2.5 text-sm text-og01-text outline-none transition-colors placeholder:text-og01-text-muted focus:border-[#8B5CF6]/60" />
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2.5">
              <button onClick={() => setIsDialogOpen(false)}
                className="rounded-[10px] border border-white/[0.07] bg-og01-card px-4 py-2.5 text-sm font-semibold text-og01-text transition-colors hover:border-white/[0.12]">
                Cancel
              </button>
              <button type="submit" onClick={handleSaveChanges} disabled={saving}
                className="rounded-[10px] border-none px-4 py-2.5 text-sm font-semibold text-white transition-all hover:brightness-105 disabled:opacity-60"
                style={{ background: GRADIENT, boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
